/**
 * Quién ve el botón de Nóminas en el menú lateral.
 *
 * Hay DOS cosas distintas, y mezclarlas fue un error real: **tener la feature
 * concedida** (un permiso del usuario) y que **su conector la soporte**
 * (`feature_nominas` es de tipo `cdb_type` y hoy solo está en A3 y Aplifisa).
 *
 * El botón solo se enseña cuando el módulo funciona de verdad: `got_feature` ya resuelve
 * las dos condiciones contra la empresa activa. Antes bastaba con no ser cliente de un
 * distribuidor, y un usuario SIN la feature en una empresa A3 veía el botón y aterrizaba
 * en una pantalla que le decía «no está disponible para A3» — justo el único programa
 * contable donde sí lo está.
 *
 * La pantalla de «no disponible» (`NominaNotAvailable`) sigue haciendo falta para otra
 * situación: la de quien YA está dentro del módulo y cambia a una empresa sin soporte.
 * Ahí no vale con esconder el botón — hay que explicarle por qué desapareció lo que
 * estaba usando.
 */

/** Distribuidores que venden su propio producto: no se les anuncia nada nuestro. */
const DISTRIBUTOR_CODE_LIST = ['glasof', 'sage_50', 'docunecta'];
const WHITE_BRAND_CODE_LIST = ['glasof', 'sdelsol', 'oxigen', 'gestoriamoro'];
const DISTRIBUTOR_CLIENT_FEATURE = 'feature_cliente_glasof';

export interface NominaMenuUser {
  sender_docs_user_distributor_code?: string;
  white_brand_code?: string;
  available_features?: Record<string, boolean>;
}

/**
 * @param hasNominaFeature si el usuario tiene concedida `feature_nominas`. Llega de
 *   fuera porque se resuelve contra la empresa activa (`got_feature`), que es del store.
 */
export function canSeeNominasMenuEntry(
  userData: NominaMenuUser,
  hasNominaFeature: boolean,
): boolean {
  if (!userData) return false;
  if (!hasNominaFeature) return false;
  const featureMap = userData.available_features || {};
  const isDistributorClient =
    DISTRIBUTOR_CODE_LIST.indexOf(userData.sender_docs_user_distributor_code || '') !== -1
    || WHITE_BRAND_CODE_LIST.indexOf(userData.white_brand_code || '') !== -1
    || Boolean(featureMap[DISTRIBUTOR_CLIENT_FEATURE]);
  return !isDistributorClient;
}

/** Lo que hace falta del permiso de la empresa activa (`permissions/permission`). */
export interface NominaCompanyPermission {
  general?: { can_admin?: boolean };
  is_employee?: boolean;
}

/**
 * Quién ve el botón «Configuración» del listado de nóminas: los mismos a los que la barra
 * enseña «editar empresa» (`App.vue`: `can_admin || is_employee`), que es donde vive esa
 * configuración, y el staff. El backend lo vuelve a comprobar al guardar
 * (`can_edit_company_settings`); esto solo evita enseñar un botón que no va a servir.
 */
export function canConfigureNominaCompany(
  permission: NominaCompanyPermission,
  userData: { is_staff?: boolean },
): boolean {
  if (userData && userData.is_staff) return true;
  if (!permission) return false;
  return Boolean((permission.general && permission.general.can_admin) || permission.is_employee);
}
